import type { UseQueryResult } from '@tanstack/react-query';
import { AlertTriangle, OctagonX, Sparkles } from 'lucide-react';

import { Tile, TileBody, TileHeader } from '../../../components/shared';
import { Skeleton } from '../../../components/ui';
import { useT } from '../../../i18n';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { KhoiLoi } from '../../nhat-ky-truy-vet/chung/components/KhoiLoi';
import { DongHanMuc } from '../../tenant/components/ChiTietHanMucToken';
import { TomTatTokenAi } from '../../tenant/components/TomTatTokenAi';
import { hienPhanTram, mucToken } from '../../tenant/nghiep-vu/han-muc-token';
import type { AiTokenQuota } from '../../tenant/types';

/**
 * Tóm tắt luôn hiện trên hàng thẻ: còn lại trên tổng, thanh tiến độ, đã dùng, ngày về 0, hạn mức của gói; từ 80% có dải
 * cảnh báo, đã chặn có dải đỏ nói rõ cái gì dừng, tới bao giờ và cái gì vẫn chạy.
 */
export function TheTomTatToken({ q }: { q: UseQueryResult<AiTokenQuota, unknown> }) {
  const { t, locale } = useT();
  const d = q.data;
  const muc = d ? mucToken(d) : null;

  return (
    <Tile>
      <TileHeader icon={<Sparkles size={16} />} tone="primary" title={t('hanMucToken.toChucTieuDe')} />
      <TileBody className="space-y-3">
        {d ? (
          <>
            <TomTatTokenAi q={d} tranPhanTram />
            <DongHanMuc q={d} doiTuong="to-chuc" />
            {muc === 'da-chan' && (
              <div role="alert" className="flex items-start gap-2 rounded border border-danger-200 bg-danger-50 px-3 py-2.5">
                <OctagonX size={16} className="mt-0.5 shrink-0 text-danger-600" />
                <div className="min-w-0 space-y-0.5 text-sm text-danger-700">
                  <p className="font-semibold">{t('hanMucToken.toChucDaChanTieuDe')}</p>
                  <p className="leading-relaxed">{t('hanMucToken.toChucDaChan', { ngay: ngayVN(d.resets_at) })}</p>
                </div>
              </div>
            )}
            {muc === 'canh-bao' && (
              <div role="status" className="flex items-start gap-2 rounded border border-warning-200 bg-warning-50 px-3 py-2.5">
                <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning-600" />
                <p className="min-w-0 text-sm leading-relaxed text-warning-700">
                  {hienPhanTram(d.used_percent).vuot
                    ? t('manTokenAi.canhBaoVuot', { ngay: ngayVN(d.resets_at) })
                    : t('hanMucToken.toChucCanhBao', { so: soVN(d.used_percent ?? 0, locale), ngay: ngayVN(d.resets_at) })}
                </p>
              </div>
            )}
          </>
        ) : q.error ? (
          <KhoiLoi gon loi={q.error} onThuLai={() => void q.refetch()} />
        ) : (
          <div aria-busy="true" aria-label={t('hanMucToken.dangTai')} className="space-y-3">
            <Skeleton className="h-7 w-56 rounded" />
            <Skeleton className="h-2 w-full rounded-full" />
            <Skeleton className="h-4 w-64 rounded" />
          </div>
        )}
      </TileBody>
    </Tile>
  );
}
